import React from 'react';
import { Ship, Anchor, FileText, Snowflake, Warehouse } from 'lucide-react';
import DirectServiceTemplate from '../components/DirectServiceTemplate';

const accent = {
  heroBg: 'bg-[radial-gradient(circle_at_top,rgba(37,99,235,0.12),transparent_70%)]',
  pill: 'bg-blue-50 text-blue-700 border-blue-100',
  cta: 'bg-[#1d4ed8] hover:bg-[#1e40af] shadow-blue-900/15',
  kpiBox: 'bg-blue-50 border-blue-100 text-blue-700',
  polyline: '#1d4ed8',
  inputFocus: 'focus:border-blue-400',
  orderCta: 'bg-blue-600 hover:bg-blue-700 text-white',
  packageActive: 'border-blue-200 bg-blue-50/40',
  toggleOn: 'border-blue-200 bg-blue-50/40',
  switchTrack: 'bg-blue-600 border-blue-600',
  switchKnobOn: 'bg-white translate-x-0',
  stepBox: 'bg-blue-50 border-blue-100 text-blue-700',
  modalHeader: 'bg-gradient-to-l from-blue-700 to-sky-600',
};

const meta = {
  Icon: Ship,
  badge: 'خدمات مستقیم — دریایی',
  title: 'دریایی',
  subtitle:
    'برای بارهای حجیم، کانتینری یا پروژه‌ای: رزرو کشتی/فضا، مدیریت اسناد (B/L)، هماهنگی بندری، و اتصال به حمل داخلی در کریدور جنوب-شمال.',
  quickTitle: 'سرویس کانتینری و فله با SLA',
  quickDesc: 'گزینه‌های استاندارد/اقتصادی برای کاهش هزینه، با کنترل اسناد و زمان‌بندی ورود/خروج بندری.',
  sla: '۲۴ تا ۷۲ ساعت',
  baseDays: 9,
  baseCost: 120,
  placeholders: {
    origin: 'مثلاً: بندر شهید رجایی (بندرعباس)',
    destination: 'مثلاً: انبار مقصد (تهران)',
    cargo: 'مثلاً: قطعات صنعتی / مواد اولیه / کالاهای کانتینری',
  },
};

const packages = [
  {
    key: 'economy',
    title: 'اقتصادی',
    desc: 'زمان‌بندی منعطف‌تر، مناسب بارهای حجیم با حساسیت زمانی کمتر.',
    badge: 'کم‌هزینه',
    badgeCls: 'bg-blue-50 text-blue-700 border-blue-100',
    baseDays: 11,
    baseCost: 95,
  },
  {
    key: 'standard',
    title: 'استاندارد',
    desc: 'تعادل زمان/هزینه، کنترل اسناد و هماهنگی بندری.',
    badge: 'پیشنهادی',
    badgeCls: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    baseDays: 9,
    baseCost: 120,
  },
  {
    key: 'priority',
    title: 'اولویت‌دار',
    desc: 'اولویت در رزرو، پیگیری سریع‌تر و هماهنگی نزدیک‌تر با بندر/اپراتور.',
    badge: 'سریع‌تر',
    badgeCls: 'bg-amber-50 text-amber-700 border-amber-100',
    baseDays: 7,
    baseCost: 160,
  },
];

const capabilities = [
  {
    title: 'رزرو و برنامه‌ریزی',
    desc: 'انتخاب سرویس (لاینر/فله/پروژه‌ای)، مدیریت تایم‌لاین و اتصال به حمل داخلی.',
    items: ['رزرو کانتینر/فضا', 'برنامه حرکت', 'اتصال به ریلی/جاده‌ای', 'اعلان‌های مرحله‌ای'],
  },
  {
    title: 'اسناد و تطبیق',
    desc: 'کنترل مدارک جهت کاهش توقف بندری و برگشت پرونده.',
    items: ['B/L', 'Packing List', 'Invoice', 'مجوزهای کالا', 'کنترل Incoterms'],
  },
  {
    title: 'کالاهای خاص',
    desc: 'پشتیبانی از محموله‌های حساس با فرآیندهای کنترل‌شده.',
    items: ['یخچالی (Reefer)', 'خطرناک (DG)', 'ابعاد خاص', 'پروژه‌ای'],
  },
];

const documents = [
  {
    title: 'بارنامه دریایی (B/L)',
    desc: 'اطلاعات فرستنده/گیرنده، بندر بارگیری/تخلیه، کانتینرها و شرایط حمل.',
    state: 'الزامی',
    stateCls: 'bg-white text-gray-700 border-gray-100',
    tags: ['اسناد حمل', 'بندر', 'کانتینر'],
  },
  {
    title: 'پکینگ لیست',
    desc: 'لیست بسته‌بندی/تعداد/وزن/ابعاد برای تطبیق در بندر و ترخیص.',
    state: 'الزامی',
    stateCls: 'bg-white text-gray-700 border-gray-100',
    tags: ['اسناد پایه', 'کنترل'],
  },
  {
    title: 'مجوزهای کالا (در صورت نیاز)',
    desc: 'بهداشت/قرنطینه/استاندارد یا مجوزهای خاص مرتبط با HS Code.',
    state: 'وابسته به کالا',
    stateCls: 'bg-amber-50 text-amber-700 border-amber-100',
    tags: ['مجوز', 'HS', 'کنترل ریسک'],
  },
  {
    title: 'بیمه‌نامه',
    desc: 'پیشنهادی برای پوشش ریسک خسارت/تأخیر/شرایط ویژه.',
    state: 'پیشنهادی',
    stateCls: 'bg-blue-50 text-blue-700 border-blue-100',
    tags: ['بیمه', 'ریسک'],
  },
];

const processSteps = [
  { title: 'ثبت RFQ', desc: 'مبدا/مقصد، نوع کالا، وزن/حجم، و نیازهای ویژه را اعلام کنید.' },
  { title: 'بررسی ظرفیت و برنامه حرکت', desc: 'لاین/مسیر پیشنهادی و زمان‌بندی تخمینی ارائه می‌شود.' },
  { title: 'کنترل و تکمیل اسناد', desc: 'چک‌لیست مدارک برای کاهش توقف بندری و خطا.' },
  { title: 'عملیات بندری و تحویل', desc: 'هماهنگی ورود/خروج و انتقال به حمل داخلی (ریلی/جاده‌ای).' },
  { title: 'رهگیری و گزارش', desc: 'مشاهده وضعیت و ETA به‌صورت مرحله‌ای در سامانه رهگیری.' },
];

const providers = [
  {
    name: 'اپراتور بندری — سرویس کانتینری',
    Icon: Anchor,
    badges: ['کانتینری', 'برنامه حرکت', 'B/L'],
    note: 'مناسب برای محموله‌های کانتینری با برنامه زمانی مشخص و نیاز به کنترل دقیق اسناد.',
    score: 4.6,
    scoreCls: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    response: 'کمتر از ۶ ساعت',
    coverage: 'بنادر جنوبی',
    capacity: 'بالا',
  },
  {
    name: 'خدمات ریفر (سردخانه/یخچالی)',
    Icon: Snowflake,
    badges: ['Reefer', 'کنترل دما', 'SLA'],
    note: 'برای کالاهای حساس به دما با ثبت و پایش شرایط نگهداری.',
    score: 4.4,
    scoreCls: 'bg-blue-50 text-blue-700 border-blue-100',
    response: '۶ تا ۱۲ ساعت',
    coverage: 'کریدور جنوبی',
    capacity: 'متوسط',
  },
  {
    name: 'کارگزار اسناد و تطبیق بندری',
    Icon: FileText,
    badges: ['چک‌لیست', 'کاهش برگشت', 'ترخیص'],
    note: 'تکمیل/کنترل مدارک و هماهنگی با ذی‌نفعان برای جلوگیری از توقف.',
    score: 4.2,
    scoreCls: 'bg-amber-50 text-amber-700 border-amber-100',
    response: 'کمتر از ۱ روز',
    coverage: 'ملی',
    capacity: 'قابل افزایش',
  },
  {
    name: 'انبارداری و خدمات ارزش افزوده',
    Icon: Warehouse,
    badges: ['Cross-dock', 'Packing', 'برچسب‌گذاری'],
    note: 'برای تجمیع/تفکیک، بسته‌بندی، و آماده‌سازی ارسال داخلی پس از تخلیه بندری.',
    score: 4.1,
    scoreCls: 'bg-gray-50 text-gray-700 border-gray-100',
    response: 'کمتر از ۱ روز',
    coverage: 'تهران/بندر',
    capacity: 'متوسط',
  },
];

const hubs = [
  { name: 'بندر شهید رجایی (بندرعباس)', lat: 27.11, lng: 56.06, note: 'هاب کانتینری/ترانزیت' },
  { name: 'بندر چابهار', lat: 25.29, lng: 60.62, note: 'دسترسی اقیانوسی' },
  { name: 'بندر انزلی', lat: 37.47, lng: 49.46, note: 'اتصال شمالی' },
  { name: 'منطقه تهران (لجستیک)', lat: 35.72, lng: 51.4, note: 'تجمیع و توزیع' },
];

const faqs = [
  { q: 'چه زمانی حمل دریایی مناسب‌تر است؟', a: 'برای بارهای حجیم/کانتینری و زمانی که هزینه اولویت بالاتری نسبت به سرعت دارد.' },
  { q: 'چه مدارکی بیشترین توقف را ایجاد می‌کند؟', a: 'ناقص بودن B/L، عدم تطبیق پکینگ لیست/فاکتور، و نبود مجوزهای کالا باعث توقف می‌شود.' },
  { q: 'آیا امکان اتصال به ریلی/جاده‌ای وجود دارد؟', a: 'بله، در کریدور جنوب-شمال معمولاً پس از تخلیه بندری، اتصال به ریلی/جاده‌ای انجام می‌شود.' },
  { q: 'برای ریفر چه کنترل‌هایی لازم است؟', a: 'ثبت دمای تنظیمی، کنترل سلامت کانتینر، و گزارش‌گیری در نقاط تحویل/تحویل‌گیری.' },
];

export default function DirectMaritime() {
  return (
    <DirectServiceTemplate
      meta={meta}
      accent={accent}
      formDefaults={{
        origin: 'بندر شهید رجایی (بندرعباس)',
        destination: 'انبار مقصد (تهران)',
        cargo: 'قطعات صنعتی (پالت)',
        incoterm: 'CPT',
        weightKg: 18400,
        volumeM3: 38,
        isExpress: false,
        needsInsurance: true,
        isSpecialHandling: false,
      }}
      packages={packages}
      capabilities={capabilities}
      documents={documents}
      processSteps={processSteps}
      providers={providers}
      hubs={hubs}
      faqs={faqs}
    />
  );
}

